import {
  TwirpContext,
  TwirpServer,
  RouterEvents,
  TwirpError,
  TwirpErrorCode,
  Interceptor,
  TwirpContentType,
  chainInterceptors,
} from "twirp-ts";
import {
  OrderHistoryReq,
  OrderHistoryResp,
  OrderReq,
  OrderResp,
  OrderToCheckReq,
  OrderToCheckResp,
  XvOrdersReq,
  XvOrdersResp,
  XvSetOrderStatusReq,
  XvSetOrderStatusResp,
  OrdersReq,
  OrdersResp,
  DeleteFileReq,
  DeleteFileResp,
  AddMoneyReq,
  AddMoneyResp,
  OrderToAdmReq,
  OrderToAdmResp,
  UpdateOrderReq,
  UpdateOrderResp,
  OrderPayedReq,
  OrderPayedResp,
  OrderAcceptPriceReq,
  OrderAcceptPriceResp,
  XvOrdersHistoryReq,
  XvOrdersHistoryResp,
  XvOrderReq,
  XvOrderResp,
  XvUpdateOrderReq,
  XvUpdateOrderResp,
  XvDeleteFileReq,
  XvDeleteFileResp,
  UsersReq,
  UsersResp,
  XvAddFilesReq,
  XvAddFilesResp,
  OrderHistoryToAdminReq,
  OrderHistoryToAdminResp,
  XvUserOrdersReq,
  XvUserOrdersResp,
  UserReq,
  UserResp,
  XvOrderPayNotifyReq,
  XvOrderPayNotifyResp,
  AuthRequest,
  AuthResponse,
} from "./prapi";

//==================================//
//          Client Code             //
//==================================//

interface Rpc {
  request(
    service: string,
    method: string,
    contentType: "application/json" | "application/protobuf",
    data: object | Uint8Array
  ): Promise<object | Uint8Array>;
}

export interface UserClient {
  OrderHistory(request: OrderHistoryReq): Promise<OrderHistoryResp>;
  Order(request: OrderReq): Promise<OrderResp>;
  OrderToCheck(request: OrderToCheckReq): Promise<OrderToCheckResp>;
}

export class UserClientJSON implements UserClient {
  private readonly rpc: Rpc;
  constructor(rpc: Rpc) {
    this.rpc = rpc;
    this.OrderHistory.bind(this);
    this.Order.bind(this);
    this.OrderToCheck.bind(this);
  }
  OrderHistory(request: OrderHistoryReq): Promise<OrderHistoryResp> {
    const data = OrderHistoryReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.User",
      "OrderHistory",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      OrderHistoryResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  Order(request: OrderReq): Promise<OrderResp> {
    const data = OrderReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.User",
      "Order",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      OrderResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  OrderToCheck(request: OrderToCheckReq): Promise<OrderToCheckResp> {
    const data = OrderToCheckReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.User",
      "OrderToCheck",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      OrderToCheckResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }
}

export class UserClientProtobuf implements UserClient {
  private readonly rpc: Rpc;
  constructor(rpc: Rpc) {
    this.rpc = rpc;
    this.OrderHistory.bind(this);
    this.Order.bind(this);
    this.OrderToCheck.bind(this);
  }
  OrderHistory(request: OrderHistoryReq): Promise<OrderHistoryResp> {
    const data = OrderHistoryReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.User",
      "OrderHistory",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      OrderHistoryResp.fromBinary(data as Uint8Array)
    );
  }

  Order(request: OrderReq): Promise<OrderResp> {
    const data = OrderReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.User",
      "Order",
      "application/protobuf",
      data
    );
    return promise.then((data) => OrderResp.fromBinary(data as Uint8Array));
  }

  OrderToCheck(request: OrderToCheckReq): Promise<OrderToCheckResp> {
    const data = OrderToCheckReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.User",
      "OrderToCheck",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      OrderToCheckResp.fromBinary(data as Uint8Array)
    );
  }
}

//==================================//
//          Server Code             //
//==================================//

export interface UserTwirp<T extends TwirpContext = TwirpContext> {
  OrderHistory(ctx: T, request: OrderHistoryReq): Promise<OrderHistoryResp>;
  Order(ctx: T, request: OrderReq): Promise<OrderResp>;
  OrderToCheck(ctx: T, request: OrderToCheckReq): Promise<OrderToCheckResp>;
}

export enum UserMethod {
  OrderHistory = "OrderHistory",
  Order = "Order",
  OrderToCheck = "OrderToCheck",
}

export const UserMethodList = [
  UserMethod.OrderHistory,
  UserMethod.Order,
  UserMethod.OrderToCheck,
];

export function createUserServer<T extends TwirpContext = TwirpContext>(
  service: UserTwirp<T>
) {
  return new TwirpServer<UserTwirp, T>({
    service,
    packageName: "pr.v3",
    serviceName: "User",
    methodList: UserMethodList,
    matchRoute: matchUserRoute,
  });
}

function matchUserRoute<T extends TwirpContext = TwirpContext>(
  method: string,
  events: RouterEvents<T>
) {
  switch (method) {
    case "OrderHistory":
      return async (
        ctx: T,
        service: UserTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, OrderHistoryReq, OrderHistoryResp>[]
      ) => {
        ctx = { ...ctx, methodName: "OrderHistory" };
        await events.onMatch(ctx);
        return handleUserOrderHistoryRequest(ctx, service, data, interceptors);
      };
    case "Order":
      return async (
        ctx: T,
        service: UserTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, OrderReq, OrderResp>[]
      ) => {
        ctx = { ...ctx, methodName: "Order" };
        await events.onMatch(ctx);
        return handleUserOrderRequest(ctx, service, data, interceptors);
      };
    case "OrderToCheck":
      return async (
        ctx: T,
        service: UserTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, OrderToCheckReq, OrderToCheckResp>[]
      ) => {
        ctx = { ...ctx, methodName: "OrderToCheck" };
        await events.onMatch(ctx);
        return handleUserOrderToCheckRequest(ctx, service, data, interceptors);
      };
    default:
      events.onNotFound();
      const msg = `no handler found`;
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleUserOrderHistoryRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderHistoryReq, OrderHistoryResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleUserOrderHistoryJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleUserOrderHistoryProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleUserOrderRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderReq, OrderResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleUserOrderJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleUserOrderProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleUserOrderToCheckRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderToCheckReq, OrderToCheckResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleUserOrderToCheckJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleUserOrderToCheckProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}
async function handleUserOrderHistoryJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderHistoryReq, OrderHistoryResp>[]
) {
  let request: OrderHistoryReq;
  let response: OrderHistoryResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = OrderHistoryReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderHistoryReq,
      OrderHistoryResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderHistory(ctx, inputReq);
    });
  } else {
    response = await service.OrderHistory(ctx, request!);
  }

  return JSON.stringify(
    OrderHistoryResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleUserOrderJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderReq, OrderResp>[]
) {
  let request: OrderReq;
  let response: OrderResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = OrderReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderReq,
      OrderResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Order(ctx, inputReq);
    });
  } else {
    response = await service.Order(ctx, request!);
  }

  return JSON.stringify(
    OrderResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleUserOrderToCheckJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderToCheckReq, OrderToCheckResp>[]
) {
  let request: OrderToCheckReq;
  let response: OrderToCheckResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = OrderToCheckReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderToCheckReq,
      OrderToCheckResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderToCheck(ctx, inputReq);
    });
  } else {
    response = await service.OrderToCheck(ctx, request!);
  }

  return JSON.stringify(
    OrderToCheckResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}
async function handleUserOrderHistoryProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderHistoryReq, OrderHistoryResp>[]
) {
  let request: OrderHistoryReq;
  let response: OrderHistoryResp;

  try {
    request = OrderHistoryReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderHistoryReq,
      OrderHistoryResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderHistory(ctx, inputReq);
    });
  } else {
    response = await service.OrderHistory(ctx, request!);
  }

  return Buffer.from(OrderHistoryResp.toBinary(response));
}

async function handleUserOrderProtobuf<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderReq, OrderResp>[]
) {
  let request: OrderReq;
  let response: OrderResp;

  try {
    request = OrderReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderReq,
      OrderResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Order(ctx, inputReq);
    });
  } else {
    response = await service.Order(ctx, request!);
  }

  return Buffer.from(OrderResp.toBinary(response));
}

async function handleUserOrderToCheckProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: UserTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderToCheckReq, OrderToCheckResp>[]
) {
  let request: OrderToCheckReq;
  let response: OrderToCheckResp;

  try {
    request = OrderToCheckReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderToCheckReq,
      OrderToCheckResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderToCheck(ctx, inputReq);
    });
  } else {
    response = await service.OrderToCheck(ctx, request!);
  }

  return Buffer.from(OrderToCheckResp.toBinary(response));
}

//==================================//
//          Client Code             //
//==================================//

interface Rpc {
  request(
    service: string,
    method: string,
    contentType: "application/json" | "application/protobuf",
    data: object | Uint8Array
  ): Promise<object | Uint8Array>;
}

export interface AdminClient {
  XvOrders(request: XvOrdersReq): Promise<XvOrdersResp>;
  XvSetOrderStatus(request: XvSetOrderStatusReq): Promise<XvSetOrderStatusResp>;
  Orders(request: OrdersReq): Promise<OrdersResp>;
  DeleteFile(request: DeleteFileReq): Promise<DeleteFileResp>;
  AddMoney(request: AddMoneyReq): Promise<AddMoneyResp>;
  OrderToAdm(request: OrderToAdmReq): Promise<OrderToAdmResp>;
  UpdateOrder(request: UpdateOrderReq): Promise<UpdateOrderResp>;
  OrderPayed(request: OrderPayedReq): Promise<OrderPayedResp>;
  OrderAcceptPrice(request: OrderAcceptPriceReq): Promise<OrderAcceptPriceResp>;
  XvOrdersHistory(request: XvOrdersHistoryReq): Promise<XvOrdersHistoryResp>;
  XvOrder(request: XvOrderReq): Promise<XvOrderResp>;
  XvUpdateOrder(request: XvUpdateOrderReq): Promise<XvUpdateOrderResp>;
  XvDeleteFile(request: XvDeleteFileReq): Promise<XvDeleteFileResp>;
  Users(request: UsersReq): Promise<UsersResp>;
  XvAddFiles(request: XvAddFilesReq): Promise<XvAddFilesResp>;
  OrderHistoryToAdmin(
    request: OrderHistoryToAdminReq
  ): Promise<OrderHistoryToAdminResp>;
  XvUserOrders(request: XvUserOrdersReq): Promise<XvUserOrdersResp>;
  User(request: UserReq): Promise<UserResp>;
  XvOrderPayNotify(request: XvOrderPayNotifyReq): Promise<XvOrderPayNotifyResp>;
  Auth(request: AuthRequest): Promise<AuthResponse>;
}

export class AdminClientJSON implements AdminClient {
  private readonly rpc: Rpc;
  constructor(rpc: Rpc) {
    this.rpc = rpc;
    this.XvOrders.bind(this);
    this.XvSetOrderStatus.bind(this);
    this.Orders.bind(this);
    this.DeleteFile.bind(this);
    this.AddMoney.bind(this);
    this.OrderToAdm.bind(this);
    this.UpdateOrder.bind(this);
    this.OrderPayed.bind(this);
    this.OrderAcceptPrice.bind(this);
    this.XvOrdersHistory.bind(this);
    this.XvOrder.bind(this);
    this.XvUpdateOrder.bind(this);
    this.XvDeleteFile.bind(this);
    this.Users.bind(this);
    this.XvAddFiles.bind(this);
    this.OrderHistoryToAdmin.bind(this);
    this.XvUserOrders.bind(this);
    this.User.bind(this);
    this.XvOrderPayNotify.bind(this);
    this.Auth.bind(this);
  }
  XvOrders(request: XvOrdersReq): Promise<XvOrdersResp> {
    const data = XvOrdersReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvOrders",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvOrdersResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  XvSetOrderStatus(
    request: XvSetOrderStatusReq
  ): Promise<XvSetOrderStatusResp> {
    const data = XvSetOrderStatusReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvSetOrderStatus",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvSetOrderStatusResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  Orders(request: OrdersReq): Promise<OrdersResp> {
    const data = OrdersReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "Orders",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      OrdersResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  DeleteFile(request: DeleteFileReq): Promise<DeleteFileResp> {
    const data = DeleteFileReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "DeleteFile",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      DeleteFileResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  AddMoney(request: AddMoneyReq): Promise<AddMoneyResp> {
    const data = AddMoneyReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "AddMoney",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      AddMoneyResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  OrderToAdm(request: OrderToAdmReq): Promise<OrderToAdmResp> {
    const data = OrderToAdmReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "OrderToAdm",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      OrderToAdmResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  UpdateOrder(request: UpdateOrderReq): Promise<UpdateOrderResp> {
    const data = UpdateOrderReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "UpdateOrder",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      UpdateOrderResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  OrderPayed(request: OrderPayedReq): Promise<OrderPayedResp> {
    const data = OrderPayedReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "OrderPayed",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      OrderPayedResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  OrderAcceptPrice(
    request: OrderAcceptPriceReq
  ): Promise<OrderAcceptPriceResp> {
    const data = OrderAcceptPriceReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "OrderAcceptPrice",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      OrderAcceptPriceResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  XvOrdersHistory(request: XvOrdersHistoryReq): Promise<XvOrdersHistoryResp> {
    const data = XvOrdersHistoryReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvOrdersHistory",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvOrdersHistoryResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  XvOrder(request: XvOrderReq): Promise<XvOrderResp> {
    const data = XvOrderReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvOrder",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvOrderResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  XvUpdateOrder(request: XvUpdateOrderReq): Promise<XvUpdateOrderResp> {
    const data = XvUpdateOrderReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvUpdateOrder",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvUpdateOrderResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  XvDeleteFile(request: XvDeleteFileReq): Promise<XvDeleteFileResp> {
    const data = XvDeleteFileReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvDeleteFile",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvDeleteFileResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  Users(request: UsersReq): Promise<UsersResp> {
    const data = UsersReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "Users",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      UsersResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  XvAddFiles(request: XvAddFilesReq): Promise<XvAddFilesResp> {
    const data = XvAddFilesReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvAddFiles",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvAddFilesResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  OrderHistoryToAdmin(
    request: OrderHistoryToAdminReq
  ): Promise<OrderHistoryToAdminResp> {
    const data = OrderHistoryToAdminReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "OrderHistoryToAdmin",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      OrderHistoryToAdminResp.fromJson(data as any, {
        ignoreUnknownFields: true,
      })
    );
  }

  XvUserOrders(request: XvUserOrdersReq): Promise<XvUserOrdersResp> {
    const data = XvUserOrdersReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvUserOrders",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvUserOrdersResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  User(request: UserReq): Promise<UserResp> {
    const data = UserReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "User",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      UserResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  XvOrderPayNotify(
    request: XvOrderPayNotifyReq
  ): Promise<XvOrderPayNotifyResp> {
    const data = XvOrderPayNotifyReq.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvOrderPayNotify",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      XvOrderPayNotifyResp.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }

  Auth(request: AuthRequest): Promise<AuthResponse> {
    const data = AuthRequest.toJson(request, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    });
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "Auth",
      "application/json",
      data as object
    );
    return promise.then((data) =>
      AuthResponse.fromJson(data as any, { ignoreUnknownFields: true })
    );
  }
}

export class AdminClientProtobuf implements AdminClient {
  private readonly rpc: Rpc;
  constructor(rpc: Rpc) {
    this.rpc = rpc;
    this.XvOrders.bind(this);
    this.XvSetOrderStatus.bind(this);
    this.Orders.bind(this);
    this.DeleteFile.bind(this);
    this.AddMoney.bind(this);
    this.OrderToAdm.bind(this);
    this.UpdateOrder.bind(this);
    this.OrderPayed.bind(this);
    this.OrderAcceptPrice.bind(this);
    this.XvOrdersHistory.bind(this);
    this.XvOrder.bind(this);
    this.XvUpdateOrder.bind(this);
    this.XvDeleteFile.bind(this);
    this.Users.bind(this);
    this.XvAddFiles.bind(this);
    this.OrderHistoryToAdmin.bind(this);
    this.XvUserOrders.bind(this);
    this.User.bind(this);
    this.XvOrderPayNotify.bind(this);
    this.Auth.bind(this);
  }
  XvOrders(request: XvOrdersReq): Promise<XvOrdersResp> {
    const data = XvOrdersReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvOrders",
      "application/protobuf",
      data
    );
    return promise.then((data) => XvOrdersResp.fromBinary(data as Uint8Array));
  }

  XvSetOrderStatus(
    request: XvSetOrderStatusReq
  ): Promise<XvSetOrderStatusResp> {
    const data = XvSetOrderStatusReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvSetOrderStatus",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      XvSetOrderStatusResp.fromBinary(data as Uint8Array)
    );
  }

  Orders(request: OrdersReq): Promise<OrdersResp> {
    const data = OrdersReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "Orders",
      "application/protobuf",
      data
    );
    return promise.then((data) => OrdersResp.fromBinary(data as Uint8Array));
  }

  DeleteFile(request: DeleteFileReq): Promise<DeleteFileResp> {
    const data = DeleteFileReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "DeleteFile",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      DeleteFileResp.fromBinary(data as Uint8Array)
    );
  }

  AddMoney(request: AddMoneyReq): Promise<AddMoneyResp> {
    const data = AddMoneyReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "AddMoney",
      "application/protobuf",
      data
    );
    return promise.then((data) => AddMoneyResp.fromBinary(data as Uint8Array));
  }

  OrderToAdm(request: OrderToAdmReq): Promise<OrderToAdmResp> {
    const data = OrderToAdmReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "OrderToAdm",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      OrderToAdmResp.fromBinary(data as Uint8Array)
    );
  }

  UpdateOrder(request: UpdateOrderReq): Promise<UpdateOrderResp> {
    const data = UpdateOrderReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "UpdateOrder",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      UpdateOrderResp.fromBinary(data as Uint8Array)
    );
  }

  OrderPayed(request: OrderPayedReq): Promise<OrderPayedResp> {
    const data = OrderPayedReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "OrderPayed",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      OrderPayedResp.fromBinary(data as Uint8Array)
    );
  }

  OrderAcceptPrice(
    request: OrderAcceptPriceReq
  ): Promise<OrderAcceptPriceResp> {
    const data = OrderAcceptPriceReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "OrderAcceptPrice",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      OrderAcceptPriceResp.fromBinary(data as Uint8Array)
    );
  }

  XvOrdersHistory(request: XvOrdersHistoryReq): Promise<XvOrdersHistoryResp> {
    const data = XvOrdersHistoryReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvOrdersHistory",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      XvOrdersHistoryResp.fromBinary(data as Uint8Array)
    );
  }

  XvOrder(request: XvOrderReq): Promise<XvOrderResp> {
    const data = XvOrderReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvOrder",
      "application/protobuf",
      data
    );
    return promise.then((data) => XvOrderResp.fromBinary(data as Uint8Array));
  }

  XvUpdateOrder(request: XvUpdateOrderReq): Promise<XvUpdateOrderResp> {
    const data = XvUpdateOrderReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvUpdateOrder",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      XvUpdateOrderResp.fromBinary(data as Uint8Array)
    );
  }

  XvDeleteFile(request: XvDeleteFileReq): Promise<XvDeleteFileResp> {
    const data = XvDeleteFileReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvDeleteFile",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      XvDeleteFileResp.fromBinary(data as Uint8Array)
    );
  }

  Users(request: UsersReq): Promise<UsersResp> {
    const data = UsersReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "Users",
      "application/protobuf",
      data
    );
    return promise.then((data) => UsersResp.fromBinary(data as Uint8Array));
  }

  XvAddFiles(request: XvAddFilesReq): Promise<XvAddFilesResp> {
    const data = XvAddFilesReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvAddFiles",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      XvAddFilesResp.fromBinary(data as Uint8Array)
    );
  }

  OrderHistoryToAdmin(
    request: OrderHistoryToAdminReq
  ): Promise<OrderHistoryToAdminResp> {
    const data = OrderHistoryToAdminReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "OrderHistoryToAdmin",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      OrderHistoryToAdminResp.fromBinary(data as Uint8Array)
    );
  }

  XvUserOrders(request: XvUserOrdersReq): Promise<XvUserOrdersResp> {
    const data = XvUserOrdersReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvUserOrders",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      XvUserOrdersResp.fromBinary(data as Uint8Array)
    );
  }

  User(request: UserReq): Promise<UserResp> {
    const data = UserReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "User",
      "application/protobuf",
      data
    );
    return promise.then((data) => UserResp.fromBinary(data as Uint8Array));
  }

  XvOrderPayNotify(
    request: XvOrderPayNotifyReq
  ): Promise<XvOrderPayNotifyResp> {
    const data = XvOrderPayNotifyReq.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "XvOrderPayNotify",
      "application/protobuf",
      data
    );
    return promise.then((data) =>
      XvOrderPayNotifyResp.fromBinary(data as Uint8Array)
    );
  }

  Auth(request: AuthRequest): Promise<AuthResponse> {
    const data = AuthRequest.toBinary(request);
    const promise = this.rpc.request(
      "pr.v3.Admin",
      "Auth",
      "application/protobuf",
      data
    );
    return promise.then((data) => AuthResponse.fromBinary(data as Uint8Array));
  }
}

//==================================//
//          Server Code             //
//==================================//

export interface AdminTwirp<T extends TwirpContext = TwirpContext> {
  XvOrders(ctx: T, request: XvOrdersReq): Promise<XvOrdersResp>;
  XvSetOrderStatus(
    ctx: T,
    request: XvSetOrderStatusReq
  ): Promise<XvSetOrderStatusResp>;
  Orders(ctx: T, request: OrdersReq): Promise<OrdersResp>;
  DeleteFile(ctx: T, request: DeleteFileReq): Promise<DeleteFileResp>;
  AddMoney(ctx: T, request: AddMoneyReq): Promise<AddMoneyResp>;
  OrderToAdm(ctx: T, request: OrderToAdmReq): Promise<OrderToAdmResp>;
  UpdateOrder(ctx: T, request: UpdateOrderReq): Promise<UpdateOrderResp>;
  OrderPayed(ctx: T, request: OrderPayedReq): Promise<OrderPayedResp>;
  OrderAcceptPrice(
    ctx: T,
    request: OrderAcceptPriceReq
  ): Promise<OrderAcceptPriceResp>;
  XvOrdersHistory(
    ctx: T,
    request: XvOrdersHistoryReq
  ): Promise<XvOrdersHistoryResp>;
  XvOrder(ctx: T, request: XvOrderReq): Promise<XvOrderResp>;
  XvUpdateOrder(ctx: T, request: XvUpdateOrderReq): Promise<XvUpdateOrderResp>;
  XvDeleteFile(ctx: T, request: XvDeleteFileReq): Promise<XvDeleteFileResp>;
  Users(ctx: T, request: UsersReq): Promise<UsersResp>;
  XvAddFiles(ctx: T, request: XvAddFilesReq): Promise<XvAddFilesResp>;
  OrderHistoryToAdmin(
    ctx: T,
    request: OrderHistoryToAdminReq
  ): Promise<OrderHistoryToAdminResp>;
  XvUserOrders(ctx: T, request: XvUserOrdersReq): Promise<XvUserOrdersResp>;
  User(ctx: T, request: UserReq): Promise<UserResp>;
  XvOrderPayNotify(
    ctx: T,
    request: XvOrderPayNotifyReq
  ): Promise<XvOrderPayNotifyResp>;
  Auth(ctx: T, request: AuthRequest): Promise<AuthResponse>;
}

export enum AdminMethod {
  XvOrders = "XvOrders",
  XvSetOrderStatus = "XvSetOrderStatus",
  Orders = "Orders",
  DeleteFile = "DeleteFile",
  AddMoney = "AddMoney",
  OrderToAdm = "OrderToAdm",
  UpdateOrder = "UpdateOrder",
  OrderPayed = "OrderPayed",
  OrderAcceptPrice = "OrderAcceptPrice",
  XvOrdersHistory = "XvOrdersHistory",
  XvOrder = "XvOrder",
  XvUpdateOrder = "XvUpdateOrder",
  XvDeleteFile = "XvDeleteFile",
  Users = "Users",
  XvAddFiles = "XvAddFiles",
  OrderHistoryToAdmin = "OrderHistoryToAdmin",
  XvUserOrders = "XvUserOrders",
  User = "User",
  XvOrderPayNotify = "XvOrderPayNotify",
  Auth = "Auth",
}

export const AdminMethodList = [
  AdminMethod.XvOrders,
  AdminMethod.XvSetOrderStatus,
  AdminMethod.Orders,
  AdminMethod.DeleteFile,
  AdminMethod.AddMoney,
  AdminMethod.OrderToAdm,
  AdminMethod.UpdateOrder,
  AdminMethod.OrderPayed,
  AdminMethod.OrderAcceptPrice,
  AdminMethod.XvOrdersHistory,
  AdminMethod.XvOrder,
  AdminMethod.XvUpdateOrder,
  AdminMethod.XvDeleteFile,
  AdminMethod.Users,
  AdminMethod.XvAddFiles,
  AdminMethod.OrderHistoryToAdmin,
  AdminMethod.XvUserOrders,
  AdminMethod.User,
  AdminMethod.XvOrderPayNotify,
  AdminMethod.Auth,
];

export function createAdminServer<T extends TwirpContext = TwirpContext>(
  service: AdminTwirp<T>
) {
  return new TwirpServer<AdminTwirp, T>({
    service,
    packageName: "pr.v3",
    serviceName: "Admin",
    methodList: AdminMethodList,
    matchRoute: matchAdminRoute,
  });
}

function matchAdminRoute<T extends TwirpContext = TwirpContext>(
  method: string,
  events: RouterEvents<T>
) {
  switch (method) {
    case "XvOrders":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, XvOrdersReq, XvOrdersResp>[]
      ) => {
        ctx = { ...ctx, methodName: "XvOrders" };
        await events.onMatch(ctx);
        return handleAdminXvOrdersRequest(ctx, service, data, interceptors);
      };
    case "XvSetOrderStatus":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<
          T,
          XvSetOrderStatusReq,
          XvSetOrderStatusResp
        >[]
      ) => {
        ctx = { ...ctx, methodName: "XvSetOrderStatus" };
        await events.onMatch(ctx);
        return handleAdminXvSetOrderStatusRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "Orders":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, OrdersReq, OrdersResp>[]
      ) => {
        ctx = { ...ctx, methodName: "Orders" };
        await events.onMatch(ctx);
        return handleAdminOrdersRequest(ctx, service, data, interceptors);
      };
    case "DeleteFile":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, DeleteFileReq, DeleteFileResp>[]
      ) => {
        ctx = { ...ctx, methodName: "DeleteFile" };
        await events.onMatch(ctx);
        return handleAdminDeleteFileRequest(ctx, service, data, interceptors);
      };
    case "AddMoney":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, AddMoneyReq, AddMoneyResp>[]
      ) => {
        ctx = { ...ctx, methodName: "AddMoney" };
        await events.onMatch(ctx);
        return handleAdminAddMoneyRequest(ctx, service, data, interceptors);
      };
    case "OrderToAdm":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, OrderToAdmReq, OrderToAdmResp>[]
      ) => {
        ctx = { ...ctx, methodName: "OrderToAdm" };
        await events.onMatch(ctx);
        return handleAdminOrderToAdmRequest(ctx, service, data, interceptors);
      };
    case "UpdateOrder":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, UpdateOrderReq, UpdateOrderResp>[]
      ) => {
        ctx = { ...ctx, methodName: "UpdateOrder" };
        await events.onMatch(ctx);
        return handleAdminUpdateOrderRequest(ctx, service, data, interceptors);
      };
    case "OrderPayed":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, OrderPayedReq, OrderPayedResp>[]
      ) => {
        ctx = { ...ctx, methodName: "OrderPayed" };
        await events.onMatch(ctx);
        return handleAdminOrderPayedRequest(ctx, service, data, interceptors);
      };
    case "OrderAcceptPrice":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<
          T,
          OrderAcceptPriceReq,
          OrderAcceptPriceResp
        >[]
      ) => {
        ctx = { ...ctx, methodName: "OrderAcceptPrice" };
        await events.onMatch(ctx);
        return handleAdminOrderAcceptPriceRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "XvOrdersHistory":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, XvOrdersHistoryReq, XvOrdersHistoryResp>[]
      ) => {
        ctx = { ...ctx, methodName: "XvOrdersHistory" };
        await events.onMatch(ctx);
        return handleAdminXvOrdersHistoryRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "XvOrder":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, XvOrderReq, XvOrderResp>[]
      ) => {
        ctx = { ...ctx, methodName: "XvOrder" };
        await events.onMatch(ctx);
        return handleAdminXvOrderRequest(ctx, service, data, interceptors);
      };
    case "XvUpdateOrder":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, XvUpdateOrderReq, XvUpdateOrderResp>[]
      ) => {
        ctx = { ...ctx, methodName: "XvUpdateOrder" };
        await events.onMatch(ctx);
        return handleAdminXvUpdateOrderRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "XvDeleteFile":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, XvDeleteFileReq, XvDeleteFileResp>[]
      ) => {
        ctx = { ...ctx, methodName: "XvDeleteFile" };
        await events.onMatch(ctx);
        return handleAdminXvDeleteFileRequest(ctx, service, data, interceptors);
      };
    case "Users":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, UsersReq, UsersResp>[]
      ) => {
        ctx = { ...ctx, methodName: "Users" };
        await events.onMatch(ctx);
        return handleAdminUsersRequest(ctx, service, data, interceptors);
      };
    case "XvAddFiles":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, XvAddFilesReq, XvAddFilesResp>[]
      ) => {
        ctx = { ...ctx, methodName: "XvAddFiles" };
        await events.onMatch(ctx);
        return handleAdminXvAddFilesRequest(ctx, service, data, interceptors);
      };
    case "OrderHistoryToAdmin":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<
          T,
          OrderHistoryToAdminReq,
          OrderHistoryToAdminResp
        >[]
      ) => {
        ctx = { ...ctx, methodName: "OrderHistoryToAdmin" };
        await events.onMatch(ctx);
        return handleAdminOrderHistoryToAdminRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "XvUserOrders":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, XvUserOrdersReq, XvUserOrdersResp>[]
      ) => {
        ctx = { ...ctx, methodName: "XvUserOrders" };
        await events.onMatch(ctx);
        return handleAdminXvUserOrdersRequest(ctx, service, data, interceptors);
      };
    case "User":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, UserReq, UserResp>[]
      ) => {
        ctx = { ...ctx, methodName: "User" };
        await events.onMatch(ctx);
        return handleAdminUserRequest(ctx, service, data, interceptors);
      };
    case "XvOrderPayNotify":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<
          T,
          XvOrderPayNotifyReq,
          XvOrderPayNotifyResp
        >[]
      ) => {
        ctx = { ...ctx, methodName: "XvOrderPayNotify" };
        await events.onMatch(ctx);
        return handleAdminXvOrderPayNotifyRequest(
          ctx,
          service,
          data,
          interceptors
        );
      };
    case "Auth":
      return async (
        ctx: T,
        service: AdminTwirp,
        data: Buffer,
        interceptors?: Interceptor<T, AuthRequest, AuthResponse>[]
      ) => {
        ctx = { ...ctx, methodName: "Auth" };
        await events.onMatch(ctx);
        return handleAdminAuthRequest(ctx, service, data, interceptors);
      };
    default:
      events.onNotFound();
      const msg = `no handler found`;
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvOrdersRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrdersReq, XvOrdersResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvOrdersJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminXvOrdersProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvSetOrderStatusRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvSetOrderStatusReq, XvSetOrderStatusResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvSetOrderStatusJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleAdminXvSetOrderStatusProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminOrdersRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrdersReq, OrdersResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminOrdersJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminOrdersProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminDeleteFileRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, DeleteFileReq, DeleteFileResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminDeleteFileJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminDeleteFileProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminAddMoneyRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, AddMoneyReq, AddMoneyResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminAddMoneyJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminAddMoneyProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminOrderToAdmRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderToAdmReq, OrderToAdmResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminOrderToAdmJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminOrderToAdmProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminUpdateOrderRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UpdateOrderReq, UpdateOrderResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminUpdateOrderJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminUpdateOrderProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminOrderPayedRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderPayedReq, OrderPayedResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminOrderPayedJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminOrderPayedProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminOrderAcceptPriceRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderAcceptPriceReq, OrderAcceptPriceResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminOrderAcceptPriceJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleAdminOrderAcceptPriceProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvOrdersHistoryRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrdersHistoryReq, XvOrdersHistoryResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvOrdersHistoryJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleAdminXvOrdersHistoryProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvOrderRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrderReq, XvOrderResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvOrderJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminXvOrderProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvUpdateOrderRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvUpdateOrderReq, XvUpdateOrderResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvUpdateOrderJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminXvUpdateOrderProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvDeleteFileRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvDeleteFileReq, XvDeleteFileResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvDeleteFileJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminXvDeleteFileProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminUsersRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UsersReq, UsersResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminUsersJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminUsersProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvAddFilesRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvAddFilesReq, XvAddFilesResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvAddFilesJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminXvAddFilesProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminOrderHistoryToAdminRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    OrderHistoryToAdminReq,
    OrderHistoryToAdminResp
  >[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminOrderHistoryToAdminJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleAdminOrderHistoryToAdminProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvUserOrdersRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvUserOrdersReq, XvUserOrdersResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvUserOrdersJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminXvUserOrdersProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminUserRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UserReq, UserResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminUserJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminUserProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminXvOrderPayNotifyRequest<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrderPayNotifyReq, XvOrderPayNotifyResp>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminXvOrderPayNotifyJSON<T>(
        ctx,
        service,
        data,
        interceptors
      );
    case TwirpContentType.Protobuf:
      return handleAdminXvOrderPayNotifyProtobuf<T>(
        ctx,
        service,
        data,
        interceptors
      );
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}

function handleAdminAuthRequest<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, AuthRequest, AuthResponse>[]
): Promise<string | Uint8Array> {
  switch (ctx.contentType) {
    case TwirpContentType.JSON:
      return handleAdminAuthJSON<T>(ctx, service, data, interceptors);
    case TwirpContentType.Protobuf:
      return handleAdminAuthProtobuf<T>(ctx, service, data, interceptors);
    default:
      const msg = "unexpected Content-Type";
      throw new TwirpError(TwirpErrorCode.BadRoute, msg);
  }
}
async function handleAdminXvOrdersJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrdersReq, XvOrdersResp>[]
) {
  let request: XvOrdersReq;
  let response: XvOrdersResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvOrdersReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvOrdersReq,
      XvOrdersResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvOrders(ctx, inputReq);
    });
  } else {
    response = await service.XvOrders(ctx, request!);
  }

  return JSON.stringify(
    XvOrdersResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminXvSetOrderStatusJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvSetOrderStatusReq, XvSetOrderStatusResp>[]
) {
  let request: XvSetOrderStatusReq;
  let response: XvSetOrderStatusResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvSetOrderStatusReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvSetOrderStatusReq,
      XvSetOrderStatusResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvSetOrderStatus(ctx, inputReq);
    });
  } else {
    response = await service.XvSetOrderStatus(ctx, request!);
  }

  return JSON.stringify(
    XvSetOrderStatusResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminOrdersJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrdersReq, OrdersResp>[]
) {
  let request: OrdersReq;
  let response: OrdersResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = OrdersReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrdersReq,
      OrdersResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Orders(ctx, inputReq);
    });
  } else {
    response = await service.Orders(ctx, request!);
  }

  return JSON.stringify(
    OrdersResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminDeleteFileJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, DeleteFileReq, DeleteFileResp>[]
) {
  let request: DeleteFileReq;
  let response: DeleteFileResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = DeleteFileReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      DeleteFileReq,
      DeleteFileResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.DeleteFile(ctx, inputReq);
    });
  } else {
    response = await service.DeleteFile(ctx, request!);
  }

  return JSON.stringify(
    DeleteFileResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminAddMoneyJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, AddMoneyReq, AddMoneyResp>[]
) {
  let request: AddMoneyReq;
  let response: AddMoneyResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = AddMoneyReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      AddMoneyReq,
      AddMoneyResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.AddMoney(ctx, inputReq);
    });
  } else {
    response = await service.AddMoney(ctx, request!);
  }

  return JSON.stringify(
    AddMoneyResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminOrderToAdmJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderToAdmReq, OrderToAdmResp>[]
) {
  let request: OrderToAdmReq;
  let response: OrderToAdmResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = OrderToAdmReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderToAdmReq,
      OrderToAdmResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderToAdm(ctx, inputReq);
    });
  } else {
    response = await service.OrderToAdm(ctx, request!);
  }

  return JSON.stringify(
    OrderToAdmResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminUpdateOrderJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UpdateOrderReq, UpdateOrderResp>[]
) {
  let request: UpdateOrderReq;
  let response: UpdateOrderResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = UpdateOrderReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      UpdateOrderReq,
      UpdateOrderResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.UpdateOrder(ctx, inputReq);
    });
  } else {
    response = await service.UpdateOrder(ctx, request!);
  }

  return JSON.stringify(
    UpdateOrderResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminOrderPayedJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderPayedReq, OrderPayedResp>[]
) {
  let request: OrderPayedReq;
  let response: OrderPayedResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = OrderPayedReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderPayedReq,
      OrderPayedResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderPayed(ctx, inputReq);
    });
  } else {
    response = await service.OrderPayed(ctx, request!);
  }

  return JSON.stringify(
    OrderPayedResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminOrderAcceptPriceJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderAcceptPriceReq, OrderAcceptPriceResp>[]
) {
  let request: OrderAcceptPriceReq;
  let response: OrderAcceptPriceResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = OrderAcceptPriceReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderAcceptPriceReq,
      OrderAcceptPriceResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderAcceptPrice(ctx, inputReq);
    });
  } else {
    response = await service.OrderAcceptPrice(ctx, request!);
  }

  return JSON.stringify(
    OrderAcceptPriceResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminXvOrdersHistoryJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrdersHistoryReq, XvOrdersHistoryResp>[]
) {
  let request: XvOrdersHistoryReq;
  let response: XvOrdersHistoryResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvOrdersHistoryReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvOrdersHistoryReq,
      XvOrdersHistoryResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvOrdersHistory(ctx, inputReq);
    });
  } else {
    response = await service.XvOrdersHistory(ctx, request!);
  }

  return JSON.stringify(
    XvOrdersHistoryResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminXvOrderJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrderReq, XvOrderResp>[]
) {
  let request: XvOrderReq;
  let response: XvOrderResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvOrderReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvOrderReq,
      XvOrderResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvOrder(ctx, inputReq);
    });
  } else {
    response = await service.XvOrder(ctx, request!);
  }

  return JSON.stringify(
    XvOrderResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminXvUpdateOrderJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvUpdateOrderReq, XvUpdateOrderResp>[]
) {
  let request: XvUpdateOrderReq;
  let response: XvUpdateOrderResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvUpdateOrderReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvUpdateOrderReq,
      XvUpdateOrderResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvUpdateOrder(ctx, inputReq);
    });
  } else {
    response = await service.XvUpdateOrder(ctx, request!);
  }

  return JSON.stringify(
    XvUpdateOrderResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminXvDeleteFileJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvDeleteFileReq, XvDeleteFileResp>[]
) {
  let request: XvDeleteFileReq;
  let response: XvDeleteFileResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvDeleteFileReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvDeleteFileReq,
      XvDeleteFileResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvDeleteFile(ctx, inputReq);
    });
  } else {
    response = await service.XvDeleteFile(ctx, request!);
  }

  return JSON.stringify(
    XvDeleteFileResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminUsersJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UsersReq, UsersResp>[]
) {
  let request: UsersReq;
  let response: UsersResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = UsersReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      UsersReq,
      UsersResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Users(ctx, inputReq);
    });
  } else {
    response = await service.Users(ctx, request!);
  }

  return JSON.stringify(
    UsersResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminXvAddFilesJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvAddFilesReq, XvAddFilesResp>[]
) {
  let request: XvAddFilesReq;
  let response: XvAddFilesResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvAddFilesReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvAddFilesReq,
      XvAddFilesResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvAddFiles(ctx, inputReq);
    });
  } else {
    response = await service.XvAddFiles(ctx, request!);
  }

  return JSON.stringify(
    XvAddFilesResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminOrderHistoryToAdminJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    OrderHistoryToAdminReq,
    OrderHistoryToAdminResp
  >[]
) {
  let request: OrderHistoryToAdminReq;
  let response: OrderHistoryToAdminResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = OrderHistoryToAdminReq.fromJson(body, {
      ignoreUnknownFields: true,
    });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderHistoryToAdminReq,
      OrderHistoryToAdminResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderHistoryToAdmin(ctx, inputReq);
    });
  } else {
    response = await service.OrderHistoryToAdmin(ctx, request!);
  }

  return JSON.stringify(
    OrderHistoryToAdminResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminXvUserOrdersJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvUserOrdersReq, XvUserOrdersResp>[]
) {
  let request: XvUserOrdersReq;
  let response: XvUserOrdersResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvUserOrdersReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvUserOrdersReq,
      XvUserOrdersResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvUserOrders(ctx, inputReq);
    });
  } else {
    response = await service.XvUserOrders(ctx, request!);
  }

  return JSON.stringify(
    XvUserOrdersResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminUserJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UserReq, UserResp>[]
) {
  let request: UserReq;
  let response: UserResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = UserReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      UserReq,
      UserResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.User(ctx, inputReq);
    });
  } else {
    response = await service.User(ctx, request!);
  }

  return JSON.stringify(
    UserResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminXvOrderPayNotifyJSON<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrderPayNotifyReq, XvOrderPayNotifyResp>[]
) {
  let request: XvOrderPayNotifyReq;
  let response: XvOrderPayNotifyResp;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = XvOrderPayNotifyReq.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvOrderPayNotifyReq,
      XvOrderPayNotifyResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvOrderPayNotify(ctx, inputReq);
    });
  } else {
    response = await service.XvOrderPayNotify(ctx, request!);
  }

  return JSON.stringify(
    XvOrderPayNotifyResp.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}

async function handleAdminAuthJSON<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, AuthRequest, AuthResponse>[]
) {
  let request: AuthRequest;
  let response: AuthResponse;

  try {
    const body = JSON.parse(data.toString() || "{}");
    request = AuthRequest.fromJson(body, { ignoreUnknownFields: true });
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the json request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      AuthRequest,
      AuthResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Auth(ctx, inputReq);
    });
  } else {
    response = await service.Auth(ctx, request!);
  }

  return JSON.stringify(
    AuthResponse.toJson(response, {
      useProtoFieldName: true,
      emitDefaultValues: false,
    }) as string
  );
}
async function handleAdminXvOrdersProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrdersReq, XvOrdersResp>[]
) {
  let request: XvOrdersReq;
  let response: XvOrdersResp;

  try {
    request = XvOrdersReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvOrdersReq,
      XvOrdersResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvOrders(ctx, inputReq);
    });
  } else {
    response = await service.XvOrders(ctx, request!);
  }

  return Buffer.from(XvOrdersResp.toBinary(response));
}

async function handleAdminXvSetOrderStatusProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvSetOrderStatusReq, XvSetOrderStatusResp>[]
) {
  let request: XvSetOrderStatusReq;
  let response: XvSetOrderStatusResp;

  try {
    request = XvSetOrderStatusReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvSetOrderStatusReq,
      XvSetOrderStatusResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvSetOrderStatus(ctx, inputReq);
    });
  } else {
    response = await service.XvSetOrderStatus(ctx, request!);
  }

  return Buffer.from(XvSetOrderStatusResp.toBinary(response));
}

async function handleAdminOrdersProtobuf<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrdersReq, OrdersResp>[]
) {
  let request: OrdersReq;
  let response: OrdersResp;

  try {
    request = OrdersReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrdersReq,
      OrdersResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Orders(ctx, inputReq);
    });
  } else {
    response = await service.Orders(ctx, request!);
  }

  return Buffer.from(OrdersResp.toBinary(response));
}

async function handleAdminDeleteFileProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, DeleteFileReq, DeleteFileResp>[]
) {
  let request: DeleteFileReq;
  let response: DeleteFileResp;

  try {
    request = DeleteFileReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      DeleteFileReq,
      DeleteFileResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.DeleteFile(ctx, inputReq);
    });
  } else {
    response = await service.DeleteFile(ctx, request!);
  }

  return Buffer.from(DeleteFileResp.toBinary(response));
}

async function handleAdminAddMoneyProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, AddMoneyReq, AddMoneyResp>[]
) {
  let request: AddMoneyReq;
  let response: AddMoneyResp;

  try {
    request = AddMoneyReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      AddMoneyReq,
      AddMoneyResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.AddMoney(ctx, inputReq);
    });
  } else {
    response = await service.AddMoney(ctx, request!);
  }

  return Buffer.from(AddMoneyResp.toBinary(response));
}

async function handleAdminOrderToAdmProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderToAdmReq, OrderToAdmResp>[]
) {
  let request: OrderToAdmReq;
  let response: OrderToAdmResp;

  try {
    request = OrderToAdmReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderToAdmReq,
      OrderToAdmResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderToAdm(ctx, inputReq);
    });
  } else {
    response = await service.OrderToAdm(ctx, request!);
  }

  return Buffer.from(OrderToAdmResp.toBinary(response));
}

async function handleAdminUpdateOrderProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UpdateOrderReq, UpdateOrderResp>[]
) {
  let request: UpdateOrderReq;
  let response: UpdateOrderResp;

  try {
    request = UpdateOrderReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      UpdateOrderReq,
      UpdateOrderResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.UpdateOrder(ctx, inputReq);
    });
  } else {
    response = await service.UpdateOrder(ctx, request!);
  }

  return Buffer.from(UpdateOrderResp.toBinary(response));
}

async function handleAdminOrderPayedProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderPayedReq, OrderPayedResp>[]
) {
  let request: OrderPayedReq;
  let response: OrderPayedResp;

  try {
    request = OrderPayedReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderPayedReq,
      OrderPayedResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderPayed(ctx, inputReq);
    });
  } else {
    response = await service.OrderPayed(ctx, request!);
  }

  return Buffer.from(OrderPayedResp.toBinary(response));
}

async function handleAdminOrderAcceptPriceProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, OrderAcceptPriceReq, OrderAcceptPriceResp>[]
) {
  let request: OrderAcceptPriceReq;
  let response: OrderAcceptPriceResp;

  try {
    request = OrderAcceptPriceReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderAcceptPriceReq,
      OrderAcceptPriceResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderAcceptPrice(ctx, inputReq);
    });
  } else {
    response = await service.OrderAcceptPrice(ctx, request!);
  }

  return Buffer.from(OrderAcceptPriceResp.toBinary(response));
}

async function handleAdminXvOrdersHistoryProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrdersHistoryReq, XvOrdersHistoryResp>[]
) {
  let request: XvOrdersHistoryReq;
  let response: XvOrdersHistoryResp;

  try {
    request = XvOrdersHistoryReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvOrdersHistoryReq,
      XvOrdersHistoryResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvOrdersHistory(ctx, inputReq);
    });
  } else {
    response = await service.XvOrdersHistory(ctx, request!);
  }

  return Buffer.from(XvOrdersHistoryResp.toBinary(response));
}

async function handleAdminXvOrderProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrderReq, XvOrderResp>[]
) {
  let request: XvOrderReq;
  let response: XvOrderResp;

  try {
    request = XvOrderReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvOrderReq,
      XvOrderResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvOrder(ctx, inputReq);
    });
  } else {
    response = await service.XvOrder(ctx, request!);
  }

  return Buffer.from(XvOrderResp.toBinary(response));
}

async function handleAdminXvUpdateOrderProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvUpdateOrderReq, XvUpdateOrderResp>[]
) {
  let request: XvUpdateOrderReq;
  let response: XvUpdateOrderResp;

  try {
    request = XvUpdateOrderReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvUpdateOrderReq,
      XvUpdateOrderResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvUpdateOrder(ctx, inputReq);
    });
  } else {
    response = await service.XvUpdateOrder(ctx, request!);
  }

  return Buffer.from(XvUpdateOrderResp.toBinary(response));
}

async function handleAdminXvDeleteFileProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvDeleteFileReq, XvDeleteFileResp>[]
) {
  let request: XvDeleteFileReq;
  let response: XvDeleteFileResp;

  try {
    request = XvDeleteFileReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvDeleteFileReq,
      XvDeleteFileResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvDeleteFile(ctx, inputReq);
    });
  } else {
    response = await service.XvDeleteFile(ctx, request!);
  }

  return Buffer.from(XvDeleteFileResp.toBinary(response));
}

async function handleAdminUsersProtobuf<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UsersReq, UsersResp>[]
) {
  let request: UsersReq;
  let response: UsersResp;

  try {
    request = UsersReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      UsersReq,
      UsersResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Users(ctx, inputReq);
    });
  } else {
    response = await service.Users(ctx, request!);
  }

  return Buffer.from(UsersResp.toBinary(response));
}

async function handleAdminXvAddFilesProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvAddFilesReq, XvAddFilesResp>[]
) {
  let request: XvAddFilesReq;
  let response: XvAddFilesResp;

  try {
    request = XvAddFilesReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvAddFilesReq,
      XvAddFilesResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvAddFiles(ctx, inputReq);
    });
  } else {
    response = await service.XvAddFiles(ctx, request!);
  }

  return Buffer.from(XvAddFilesResp.toBinary(response));
}

async function handleAdminOrderHistoryToAdminProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<
    T,
    OrderHistoryToAdminReq,
    OrderHistoryToAdminResp
  >[]
) {
  let request: OrderHistoryToAdminReq;
  let response: OrderHistoryToAdminResp;

  try {
    request = OrderHistoryToAdminReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      OrderHistoryToAdminReq,
      OrderHistoryToAdminResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.OrderHistoryToAdmin(ctx, inputReq);
    });
  } else {
    response = await service.OrderHistoryToAdmin(ctx, request!);
  }

  return Buffer.from(OrderHistoryToAdminResp.toBinary(response));
}

async function handleAdminXvUserOrdersProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvUserOrdersReq, XvUserOrdersResp>[]
) {
  let request: XvUserOrdersReq;
  let response: XvUserOrdersResp;

  try {
    request = XvUserOrdersReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvUserOrdersReq,
      XvUserOrdersResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvUserOrders(ctx, inputReq);
    });
  } else {
    response = await service.XvUserOrders(ctx, request!);
  }

  return Buffer.from(XvUserOrdersResp.toBinary(response));
}

async function handleAdminUserProtobuf<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, UserReq, UserResp>[]
) {
  let request: UserReq;
  let response: UserResp;

  try {
    request = UserReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      UserReq,
      UserResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.User(ctx, inputReq);
    });
  } else {
    response = await service.User(ctx, request!);
  }

  return Buffer.from(UserResp.toBinary(response));
}

async function handleAdminXvOrderPayNotifyProtobuf<
  T extends TwirpContext = TwirpContext
>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, XvOrderPayNotifyReq, XvOrderPayNotifyResp>[]
) {
  let request: XvOrderPayNotifyReq;
  let response: XvOrderPayNotifyResp;

  try {
    request = XvOrderPayNotifyReq.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      XvOrderPayNotifyReq,
      XvOrderPayNotifyResp
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.XvOrderPayNotify(ctx, inputReq);
    });
  } else {
    response = await service.XvOrderPayNotify(ctx, request!);
  }

  return Buffer.from(XvOrderPayNotifyResp.toBinary(response));
}

async function handleAdminAuthProtobuf<T extends TwirpContext = TwirpContext>(
  ctx: T,
  service: AdminTwirp,
  data: Buffer,
  interceptors?: Interceptor<T, AuthRequest, AuthResponse>[]
) {
  let request: AuthRequest;
  let response: AuthResponse;

  try {
    request = AuthRequest.fromBinary(data);
  } catch (e) {
    if (e instanceof Error) {
      const msg = "the protobuf request could not be decoded";
      throw new TwirpError(TwirpErrorCode.Malformed, msg).withCause(e, true);
    }
  }

  if (interceptors && interceptors.length > 0) {
    const interceptor = chainInterceptors(...interceptors) as Interceptor<
      T,
      AuthRequest,
      AuthResponse
    >;
    response = await interceptor(ctx, request!, (ctx, inputReq) => {
      return service.Auth(ctx, inputReq);
    });
  } else {
    response = await service.Auth(ctx, request!);
  }

  return Buffer.from(AuthResponse.toBinary(response));
}
